import { useEffect, useRef, useState } from "react";
import { useSession, useSyncTarget } from "../session.tsx";
import { startLiveSync, type ShapeTable, type SyncStatus } from "../sync.ts";

/** Keep a screen fresh: live through the shape proxy when Electric is up, otherwise REST polling every 10 s. */
export function useLiveRefresh(refresh: () => Promise<unknown> | void, tables: readonly ShapeTable[]): SyncStatus {
  const s = useSession();
  const target = useSyncTarget();
  const fn = useRef(refresh);
  fn.current = refresh;
  const [status, setStatus] = useState<SyncStatus>({ mode: "connecting", lastUpdated: Date.now() });
  const key = tables.join(",");
  useEffect(() => {
    const sync = startLiveSync({
      tables, shapeBase: target.shapeBase(s.org.id), headers: target.headers, subscribeLocal: target.subscribeLocal,
      refresh: async () => { await fn.current(); }, onStatus: setStatus,
    });
    return () => sync.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.org.id, s.location.id, target, key]);
  return status;
}

/** Re-render every `ms` (countdowns, "updated 2 min ago"). */
export function useNow(ms = 15_000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const h = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(h); }, [ms]);
  return now;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}
